// src/App.js
import React, { useEffect, useRef, useState, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';

import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import ContactUs from './components/ContactUs';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HelpBar from './components/HelpBar';

import HomePage from './HomePage';
import PreviewGate from './components/PreviewGate';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import ForgotPassword from './ForgotPassword';
import PasswordResetConfirm from './PasswordResetConfirm';
import './App.css';
import './pages/SubscriptionPage.css';
import BestBetsPage from './pages/BestBetsPage';
import AdminOverrideBanner from './components/AdminOverrideBanner';
import PerformanceBanner from './components/PerformanceBanner';
import { useAuth } from './contexts/AuthContext';
import { useSelectedState, DEFAULT_STATE } from './contexts/SelectedStateContext';
import { VALID_STATES, getStateFromPath } from './utils/stateRouting';
import LocationStateNotice from './components/LocationStateNotice';

// Less-common routes are code-split so the initial bundle only needs
// Home/Value Finder's dependencies for first paint.
const AllAnalyzedDataView   = lazy(() => import('./components/AllAnalyzedDataView'));
const OrganizedPlayer       = lazy(() => import('./components/OrganizedPlayer'));
const TrackedBetsProofPage  = lazy(() => import('./components/TrackedBetsProofPage'));
const SubscriptionPage      = lazy(() => import('./pages/SubscriptionPage'));
const SubscriptionSuccess   = lazy(() => import('./pages/SubscriptionSuccess'));
const SubscriptionCancel    = lazy(() => import('./pages/SubscriptionCancel'));
const RedeemVoucherPage     = lazy(() => import('./pages/RedeemVoucherPage'));
const CalculatorsPage       = lazy(() => import('./pages/CalculatorsPage'));
const ArbitrageTable        = lazy(() => import('./components/ArbitrageTable'));
const OddsMovementPage      = lazy(() => import('./components/MovementPages/OddsMovementPage'));
const PointLineMovementPage = lazy(() => import('./components/MovementPages/PointLineMovementPage'));
const BarPartnerLanding     = lazy(() => import('./pages/BarPartnerLanding'));
const PromoFinderPage       = lazy(() => import('./pages/PromoFinderPage'));
const PromotionsPage        = lazy(() => import('./pages/PromotionsPage'));
const ParlayPickerPage      = lazy(() => import('./pages/ParlayPickerPage'));
const AcademyPage           = lazy(() => import('./pages/AcademyPage'));
const AcademyEvBettingPage  = lazy(() => import('./pages/AcademyEvBettingPage'));
const NFLPage                = lazy(() => import('./pages/NFLPage'));
const AdminOverridePage     = lazy(() => import('./pages/AdminOverridePage'));
const StoryblokEditorPage   = lazy(() => import('./pages/StoryblokEditorPage'));
const BlogIndexPage         = lazy(() => import('./pages/BlogIndexPage'));
const BlogArticlePage       = lazy(() => import('./pages/BlogArticlePage'));
const ResultsPage           = lazy(() => import('./pages/ResultsPage'));
const CommunityPage         = lazy(() => import('./pages/CommunityPage'));

// Minimal route-transition fallback for lazy chunks — intentionally plain,
// shown only for the brief window a code-split chunk takes to download.
function RouteFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
      <span style={{ opacity: 0.6, fontSize: '0.95rem' }}>Loading…</span>
    </div>
  );
}


function HomePageStateRoute() {
  const { state } = useParams();
  const stateCode = state?.toUpperCase();
  if (!VALID_STATES.has(stateCode)) {
    return <Navigate to="/" replace />;
  }
  return <HomePage userState={stateCode} />;
}

// Synchronous redirect from "/" to the state-specific home URL.
// Using a component (rather than a useEffect) means the redirect fires during
// the render itself — no paint-then-redirect flicker, and no reliance on
// localStorage being read correctly in an async effect.
function HomeRedirect() {
  const { selectedState } = useSelectedState();
  return <Navigate to={`/${selectedState.toLowerCase()}`} replace />;
}

// Same redirect pattern as HomeRedirect, for /value-finder with no state
// segment — avoids mounting BestBetsPage then navigating away from it.
// Preserves any query string (e.g. ?sport=... or ?bookmaker=...) so preset
// filter links still work before a state has been chosen.
function ValueFinderRedirect() {
  const { selectedState } = useSelectedState();
  const location = useLocation();
  return <Navigate to={`/value-finder/${selectedState.toLowerCase()}${location.search}`} replace />;
}

// Old "/blog" URLs now live under "/analysis" — keeps bookmarks/external
// links working client-side (render.yaml handles the server-side 301).
function BlogRedirect() {
  const { slug } = useParams();
  const location = useLocation();
  const target = slug ? `/analysis/${slug}` : '/analysis';
  return <Navigate to={`${target}${location.search}`} replace />;
}

const SUBSCRIPTION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Standalone bar/restaurant QR partner landing pages — no site nav, footer,
// or help bar so the page stays a single, low-friction conversion path.
const BAR_PARTNER_ROUTES = ['/bar-landing-page'];

function App() {
  const location = useLocation();
  const { fetchSubscriptionStatus } = useAuth();
  const { selectedState, updateState } = useSelectedState();
  const navigate = useNavigate();
  const isBarPartnerLanding = BAR_PARTNER_ROUTES.includes(location.pathname);
  // Tracks the last time we proactively re-validated subscription status
  // during active navigation. Initialized to now so the first navigation
  // doesn't duplicate AuthContext's own mount-time fetch.
  const lastSubscriptionCheck = useRef(Date.now());

  // lift gate state to top‑level so routes re‑evaluate when it changes
  // Gate temporarily disabled — always treat as passed
  const [hasPassedGate, setHasPassedGate] = useState(true);

  // called by PreviewGate when user selects state + confirms 21+
  const handleGatePass = (state) => {
    localStorage.setItem('hasPassedGate', 'true');
    localStorage.setItem('selectedState', state);
    setHasPassedGate(true);
    // Drive the URL to the state-specific homepage so Googlebot and returning
    // users land on the correct canonical state page.
    if (state) navigate(`/${state.toLowerCase()}`, { replace: true });
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    // URL → context sync: if the user lands on a state-specific URL via a
    // bookmark, browser-saved link, or the browser's address bar (e.g.
    // boostyourbets.com/fl while OH is the saved state), update the context
    // and localStorage so the Navbar and every component stay in sync with
    // what the page is actually showing.
    const urlState = getStateFromPath(location.pathname);
    if (urlState && VALID_STATES.has(urlState) && urlState !== selectedState) {
      updateState(urlState);
    }

    // Re-validate subscription status if the cached user data is stale.
    // Catches changes (cancellation, failed payment) that occurred during
    // the current session without a tab switch.
    const now = Date.now();
    if (now - lastSubscriptionCheck.current > SUBSCRIPTION_CHECK_INTERVAL) {
      const token =
        localStorage.getItem('access_token') ||
        localStorage.getItem('refresh_token');
      if (token) {
        lastSubscriptionCheck.current = now;
        fetchSubscriptionStatus();
      }
    }
  }, [location.pathname, fetchSubscriptionStatus, selectedState, updateState]);

  return (
    <div className="app-container">
      {!isBarPartnerLanding && <Navbar />}
      {!isBarPartnerLanding && <PerformanceBanner />}

      {/* show the modal on top of everything until the gate is passed */}
      {!isBarPartnerLanding && !hasPassedGate && (
        <PreviewGate isOpen={true} onGatePass={handleGatePass} />
      )}

      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* public/legal */}
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/login" element={<Navigate to="/subscribe?mode=login" replace />} />
        <Route path="/register" element={<Navigate to="/subscribe?mode=register" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:uid/:token" element={<PasswordResetConfirm />} />

        <Route path="/profile" element={<SubscriptionPage />} />
        <Route path="/subscribe" element={<SubscriptionPage />} />
        <Route path="/subscription-success" element={<SubscriptionSuccess />} />
        <Route path="/subscription-cancel" element={<SubscriptionCancel />} />
        <Route path="/redeem/:token" element={<RedeemVoucherPage />} />
        <Route path="/calculators" element={<CalculatorsPage />} />
        <Route path="/arbitrage/" element={<ArbitrageTable />} />
        <Route path="/odds-movement" element={<OddsMovementPage />} />
        <Route path="/point-line-movement" element={<PointLineMovementPage />} />
        <Route path="/value-finder" element={<ValueFinderRedirect />} />
        <Route path="/value-finder/:state" element={<BestBetsPage />} />
        <Route path="/promo-finder" element={<PromoFinderPage />} />
        <Route path="/promo-finder/:state" element={<PromoFinderPage />} />
        <Route path="/promotions" element={<PromotionsPage />} />
        <Route path="/promotions/:state" element={<PromotionsPage />} />
        <Route path="/parlay-picker" element={<ParlayPickerPage />} />
        <Route path="/academy" element={<AcademyPage />} />
        <Route path="/academy/ev-betting" element={<AcademyEvBettingPage />} />
        <Route path="/nfl" element={<NFLPage />} />
        <Route path="/results" element={<ResultsPage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/admin/override" element={<AdminOverridePage />} />
        <Route path="/admin/storyblok" element={<StoryblokEditorPage />} />
        {/* No nav link yet — direct navigation only, per request. */}
        <Route path="/analysis" element={<BlogIndexPage />} />
        <Route path="/analysis/:slug" element={<BlogArticlePage />} />
        {/* Legacy "/blog" paths — redirect to "/analysis" equivalents. */}
        <Route path="/blog" element={<BlogRedirect />} />
        <Route path="/blog/:slug" element={<BlogRedirect />} />
        <Route path="/bar-landing-page" element={<BarPartnerLanding />} />
        
                
                 
        {/* "/" always redirects to the state-specific URL via HomeRedirect.
             HomeRedirect reads from SelectedStateContext (React state), so it
             reacts to focus-event updates on mobile — eliminating the race
             condition where localStorage is null at first render. */}
        <Route path="/" element={<HomeRedirect />} />

        {/* state-specific home URLs e.g. /fl, /ny, /oh */}
        <Route path="/:state" element={<HomePageStateRoute />} />

        {/* gated routes */}
        <Route
          path="/all-data"
          element={
            hasPassedGate ? (
              <AllAnalyzedDataView userState={selectedState} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        <Route
          path="/organized-player"
          element={
            hasPassedGate ? (
              <OrganizedPlayer userState={selectedState} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        <Route
          path="/tracked-bets"
          element={
            hasPassedGate ? (
              <TrackedBetsProofPage userState={selectedState} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* catch‑all → home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>

      {!isBarPartnerLanding && <HelpBar />}
      {!isBarPartnerLanding && <Footer />}
      {/* One-time, dismissible notice shown only when the state was set via
          location detection rather than an explicit user/URL choice. */}
      {!isBarPartnerLanding && <LocationStateNotice />}
      {/* Fixed admin banner — visible only to staff when override is active */}
      {!isBarPartnerLanding && <AdminOverrideBanner />}
    </div>
  );
}

export default App;


